/*
 * The documents' frame router, in the shell chunk (DV S-12 §3.3). Every `doc.*` event — the stream of
 * batches, a draft's new form, carets, who is here, the head after a quiet spell, and the durable
 * events that say a thread, a version or the history moved — arrives through the one socket and the
 * store's `applyEvent`, which hands it here instead of reducing it. None of them changes the Zustand
 * model: a typist sends up to five frames a second, and a model that moved on each would re-render
 * every subscriber of the shell for words only the open document shows. What a frame changes is the
 * open document's own state — its collab core, its painter, its presence — which subscribes here by
 * document id while the page is mounted and is the only thing that pays for the frame.
 *
 * A document nobody listens to drops its frames: the server sends them only to a tab that has the
 * document in focus, the page that opens it buffers from its own subscription until the open answer
 * is there (DV S-00 F1), and a durable event is about a record the page reads again when it opens.
 */

import type { Applied, Model } from "../store/model.ts"
import type { DocEvent } from "./wire/events.ts"

/** Hears every `doc.*` event of one document, frames and durable events alike, in arrival order. */
export type DocListener = (event: DocEvent) => void

const listeners = new Map<number, Set<DocListener>>()

/**
 * Subscribes to one document's events until the returned function is called. The page that shows the
 * document subscribes before it focuses it, so no frame the focus brings is missed.
 */
export function listenToDocument(doc: number, listener: DocListener): () => void {
	let set = listeners.get(doc)
	if (!set) {
		set = new Set()
		listeners.set(doc, set)
	}
	set.add(listener)
	return () => {
		const current = listeners.get(doc)
		if (!current) return
		current.delete(listener)
		if (current.size === 0) listeners.delete(doc)
	}
}

/**
 * `applyEvent`'s case for the documents: the event goes to its document's listeners and the model
 * stays as it is, so `applyEvent` remains a function of the model for everything it keeps. A durable
 * event is still "applied", which is what moves the catch-up cursor past it. A listener that throws
 * is a defect of that page; it is rethrown on its own task, so the next listener and the next frame
 * are not the ones that pay for it.
 */
export function reduceDoc(model: Model, event: DocEvent): { next: Model; result: Applied } {
	const set = listeners.get(event.data.doc)
	if (set) {
		for (const listener of [...set]) {
			try {
				listener(event)
			} catch (error) {
				queueMicrotask(() => {
					throw error
				})
			}
		}
	}
	return { next: model, result: "applied" }
}
